/**
 * Canonical chart palette + defaults shared by dashboard (global + YTG) charts.
 * Colors mirror the chart.js defaults that were in use before the Phase 3 #55
 * recharts migration — preserves the visual identity for end users.
 */

// Uniform teal/cyan used by chart.js for single-dataset bar charts (Dashboard
// "Operations by tool/client", YTG "Breakdown by language").
export const BAR_FILL = "rgba(75, 192, 192, 0.7)";
/** Solid teal border color paired with {@link BAR_FILL} on bar charts. */
export const BAR_STROKE = "rgba(75, 192, 192, 1)";

// chart.js default palette — used for multi-color visuals (the user pie chart
// on the global Dashboard). Order matches chart.js' internal default colors
// so the visual identity is preserved.
/** Ordered fill colors for multi-slice pie charts (chart.js default palette). */
export const PIE_PALETTE: readonly string[] = [
    "rgba(255, 99, 132, 0.7)",  // red/pink
    "rgba(54, 162, 235, 0.7)",  // blue
    "rgba(255, 206, 86, 0.7)",  // yellow
    "rgba(75, 192, 192, 0.7)",  // teal
    "rgba(153, 102, 255, 0.7)", // purple
    "rgba(255, 159, 64, 0.7)",  // orange
];

/** Solid border colors matching {@link PIE_PALETTE}, index-for-index. */
export const PIE_STROKES: readonly string[] = [
    "rgba(255, 99, 132, 1)",
    "rgba(54, 162, 235, 1)",
    "rgba(255, 206, 86, 1)",
    "rgba(75, 192, 192, 1)",
    "rgba(153, 102, 255, 1)",
    "rgba(255, 159, 64, 1)",
];

// Operation-status colors — Chakra theme aligned, alpha 0.7 to match the
// muted look chart.js produced (it applied 0.7 fill + 1.0 border).
/** Fill color per operation status (completed, in_progress, pending, failed). */
export const STATUS_COLORS: Record<string, string> = {
    completed:   "rgba(72, 187, 120, 0.7)",   // green
    in_progress: "rgba(66, 153, 225, 0.7)",   // blue
    pending:     "rgba(237, 137, 54, 0.7)",   // orange
    failed:      "rgba(245, 101, 101, 0.7)",  // red
};

/** Solid border color per operation status, matching {@link STATUS_COLORS}. */
export const STATUS_STROKES: Record<string, string> = {
    completed:   "rgba(72, 187, 120, 1)",
    in_progress: "rgba(66, 153, 225, 1)",
    pending:     "rgba(237, 137, 54, 1)",
    failed:      "rgba(245, 101, 101, 1)",
};

// Backwards-compat alias — pre-existing CHART_PALETTE consumers (none after
// this commit, but keep export to avoid breaking imports if any slip through).
/** Backwards-compatible alias of {@link PIE_PALETTE} for legacy consumers. */
export const CHART_PALETTE = PIE_PALETTE;

/**
 * Axis tick formatter that renders only integer values, hiding fractional
 * ticks recharts may auto-generate.
 *
 * @param value - Tick value proposed by recharts.
 * @returns The integer as a string, or an empty string for non-integers.
 */
export const INTEGER_TICK_FORMATTER = (value: number): string => {
    return Number.isInteger(value) ? String(value) : "";
};

/** Shared recharts axis styling (font size, tick/axis lines). */
export const AXIS_DEFAULTS = {
    fontSize: 12,
    tickLine: false,
    axisLine: { stroke: "#CBD5E0" },
} as const;

/** Default outer margin applied to dashboard chart containers. */
export const CHART_MARGIN = { top: 10, right: 20, left: 0, bottom: 20 } as const;
